import moment from "moment";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import Toast from "../../../../parts/toast";
import { isFemaleCycleSectionEnabledFromConfig } from "../../../../utils/client-configuration-helpers";
import { getEffectiveEndDate } from "../../calendar/menstrual-cycle/menstrual-cycle.helper";
import ClientGoals from "./ClientGoals";
import ClientInformation from "./ClientInformation";
import DailyGoals from "./DailyGoals";
import FemaleCycleMenstrualIndicator from "./FemaleCycleMenstrualIndicator";
import OnboardingInfo from "./OnboardingInfo";
import OnboardingSummary from "./OnboardingSummary";
import ProfileListItem from "./ProfileListItem";

const DEFAULT_AVERAGE_PERIOD_LENGTH = 5;

const buildInitialLogFromSettings = (settings) => {
  if (!settings?.firstRegisteredPeriod) return null;

  const start = moment(settings.firstRegisteredPeriod, "YYYYMMDD");

  const end = start.clone().add(settings.averagePeriodLength - 1, "days");

  return {
    startDate: Number(start.format("YYYYMMDD")),
    endDate: Number(end.format("YYYYMMDD")),
    source: "settings",
  };
};

const toMoment = (date) => moment(date, "YYYYMMDD");

const diffDaysInclusive = (start, end) =>
  toMoment(end).diff(toMoment(start), "days") + 1;

const diffDays = (from, to) => toMoment(to).diff(toMoment(from), "days");

// Resolve a usable endDate even for legacy logs that were saved without one
// (capped at today so we don't extend periods into the future).
const resolveEffectiveEndDate = (log, averagePeriodLength) =>
  log.endDate ?? getEffectiveEndDate(log.startDate, averagePeriodLength);

const Dashboard = ({ athlete, locals }) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  const [state, setState] = React.useState({
    athleteId: athlete._id,
    goals: [],
    limitations: [],
    equipment: [],
  });

  const [shouldShowSuccessToast, setShouldShowSuccessToast] =
    React.useState(false);
  const [onboardingInfo, setOnboardingInfo] = React.useState(null);

  const { goals, equipment, limitations, _id: athleteId } = athlete;
  const coachOrganizationInformation =
    locals.coachOrganizationInformation || undefined;

  const femaleCycleTracking = isFemaleCycleSectionEnabledFromConfig(
    locals.clientConfiguration
  );

  const { femaleCycleLogs, femaleCycleSettings } = locals;
  const today = moment();

  const normalizedLogs = useMemo(() => {
    const baseLogs = femaleCycleLogs ?? [];
    const settingsLog = buildInitialLogFromSettings(femaleCycleSettings);

    if (!settingsLog) {
      // Always sort DESC by startDate
      return [...baseLogs].sort((a, b) => b.startDate - a.startDate);
    }

    const alreadyExists = baseLogs.some(
      (log) => log?.startDate === settingsLog?.startDate
    );

    const merged = alreadyExists ? baseLogs : [settingsLog, ...baseLogs];

    // Always sort DESC by startDate
    return [...merged].sort((a, b) => b.startDate - a.startDate);
  }, [femaleCycleLogs, femaleCycleSettings]);

  const averagePeriodLength =
    femaleCycleSettings?.averagePeriodLength ?? DEFAULT_AVERAGE_PERIOD_LENGTH;

  const { currentLog, lastLog } = useMemo(() => {
    if (!normalizedLogs.length) {
      return { currentLog: null, lastLog: null };
    }

    // Current log: today falls within [startDate, effectiveEnd] (inclusive).
    // Effective end falls back to startDate + averagePeriodLength - 1 (capped
    // at today) for legacy logs saved without an endDate, so old "open"
    // periods naturally retire instead of showing "currently on period"
    // forever.
    const current = normalizedLogs.find((log) => {
      if (!log.startDate) return false;
      const effectiveEnd = resolveEffectiveEndDate(log, averagePeriodLength);
      return (
        toMoment(log.startDate).isSameOrBefore(today, "day") &&
        toMoment(effectiveEnd).isSameOrAfter(today, "day")
      );
    });

    // Completed logs: effective end is strictly before today.
    const completedLogs = normalizedLogs.filter((log) => {
      if (!log.startDate) return false;
      if (toMoment(log.startDate).isAfter(today, "day")) return false;
      const effectiveEnd = resolveEffectiveEndDate(log, averagePeriodLength);
      return toMoment(effectiveEnd).isBefore(today, "day");
    });

    const last = completedLogs[0] ?? null;
    const secondToLast = completedLogs[1] ?? null;

    if (!last) {
      return { currentLog: current ?? null, lastLog: null };
    }

    const lastEnd = resolveEffectiveEndDate(last, averagePeriodLength);
    const periodLength = diffDaysInclusive(last.startDate, lastEnd);

    const secondToLastEnd = secondToLast
      ? resolveEffectiveEndDate(secondToLast, averagePeriodLength)
      : null;
    const cycleLength = secondToLastEnd
      ? diffDays(secondToLastEnd, last.startDate)
      : null;

    return {
      currentLog: current ?? null,
      lastLog: {
        ...last,
        endDate: lastEnd,
        periodLength,
        cycleLength,
      },
    };
  }, [normalizedLogs, averagePeriodLength]);

  React.useEffect(() => {
    const verified = {
      goals: typeof goals === "object" ? goals : [],
      equipment: typeof equipment === "object" ? equipment : [],
      limitations: typeof limitations === "object" ? limitations : [],
    };
    setState({
      athleteId,
      goals: verified.goals,
      equipment: verified.equipment,
      limitations: verified.limitations,
    });
    setOnboardingInfo(null);
  }, [athlete]);

  const getSelector = (type) => {
    switch (type) {
      case "goals":
        return "goalId";
      case "equipment":
        return "equipmentId";
      case "limitations":
        return "limitationId";
      default:
        return "goalId";
    }
  };

  const addItem = (type) => {
    setShouldShowSuccessToast(false);
    const dat = state[type];
    dat.push({
      [getSelector(type)]:
        state[type].length > 0
          ? state[type][state[type].length - 1][getSelector(type)] + 1
          : 1,
      name: "",
      checked: false,
      creating: true,
    });
    setState((prevState) => Object.assign({}, prevState, { [type]: dat }));
  };

  const removeItem = (type, id, creating = false) => {
    setShouldShowSuccessToast(false);
    const dat = state[type];
    const filtered = dat.filter((d) => d[getSelector(type)] != id);
    setState((prevState) =>
      Object.assign({}, prevState, {
        [type]: filtered,
        creating: false,
        editing: false,
      })
    );
    if (!creating) sendRequest({ [type]: filtered });
  };

  const sendRequest = (values) => {
    let body = {
      goals: values.goals
        ? values.goals.filter((f) => !f.creating)
        : state.goals.filter((f) => !f.creating),
      limitations: values.limitations
        ? values.limitations
        : state.limitations.filter((f) => !f.creating),
      equipment: values.equipment
        ? values.equipment
        : state.equipment.filter((f) => !f.creating),
    };

    if (coachOrganizationInformation) {
      body = { ...body, coachOrganizationInformation };
    }

    submit(`/actions/user/update-athlete-profile/${state.athleteId}`, {
      method: "POST",
      body: body,
    });

    setShouldShowSuccessToast(true);
  };

  const modifyCallback = (type, id, values) => {
    const data = state[type].map((obj) => {
      return obj[getSelector(type)] === id ? Object.assign(obj, values) : obj;
    });

    sendRequest({ [type]: data });
    const newState = Object.assign({}, state, { [type]: data });
    setState(newState);
    // Send request to API to update item...
  };

  // Imma bout to do it to them
  if (onboardingInfo) {
    return (
      <OnboardingSummary
        onboardingInfo={onboardingInfo}
        setOnboardingInfo={setOnboardingInfo}
        locals={locals}
      />
    );
  }

  return (
    <div className="dashboard">
      <section className="dashboard-panel">
        <DailyGoals locals={locals} />
        <div className="flex flex-row gap-4">
          <ClientGoals locals={locals} />
          <div>
            <OnboardingInfo
              locals={locals}
              setOnboardingInfo={setOnboardingInfo}
            />
            <ClientInformation locals={locals} />
            {femaleCycleTracking && (
              <FemaleCycleMenstrualIndicator
                currentLog={currentLog}
                lastLog={lastLog}
                athleteId={athleteId}
                query={locals?.query}
              />
            )}
          </div>
        </div>
      </section>

      <div>
        <article>
          <header>{t("dashboard.pinnedNotes")}</header>
          <ul>
            {state.goals &&
              state.goals.map((goal, idx) => (
                <ProfileListItem
                  key={idx}
                  modifyCallback={modifyCallback}
                  removeItem={removeItem}
                  type="goals"
                  showChecked
                  itemSelector={getSelector("goals")}
                  {...goal}
                />
              ))}
          </ul>
          <button data-button-type="outline" onClick={() => addItem("goals")}>
            {t("common:add")}
          </button>
        </article>
        <article>
          <header>{t("dashboard.limitations")}</header>
          <ul>
            {state.limitations &&
              state.limitations.map((limitation, idx) => (
                <ProfileListItem
                  key={idx}
                  modifyCallback={modifyCallback}
                  removeItem={removeItem}
                  type="limitations"
                  itemSelector={getSelector("limitations")}
                  {...limitation}
                />
              ))}
          </ul>
          <button
            data-button-type="outline"
            onClick={() => addItem("limitations")}
          >
            {t("common:add")}
          </button>
        </article>
        <article>
          <header>{t("dashboard.equipmentAvailable")}</header>
          <ul>
            {state.equipment &&
              state.equipment.map((equipment, idx) => (
                <ProfileListItem
                  key={idx}
                  modifyCallback={modifyCallback}
                  removeItem={removeItem}
                  type="equipment"
                  itemSelector={getSelector("equipment")}
                  {...equipment}
                />
              ))}
          </ul>
          <button
            data-button-type="outline"
            onClick={() => addItem("equipment")}
          >
            {t("common:add")}
          </button>
        </article>
      </div>
      <Toast
        open={shouldShowSuccessToast}
        message={t("dashboard.profileUpdated")}
      />
    </div>
  );
};

module.exports = Dashboard;
